/** WCAG 2.x colour helpers shared by the pipeline (validation) and the UI (theme). */

export function parseHex(hex: string): [number, number, number] {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) throw new Error(`Expected #RRGGBB colour, got "${hex}"`);
  const n = Number.parseInt(m[1]!, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function channel(c: number): number {
  const s = c / 255;
  return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
}

export function relativeLuminance(hex: string): number {
  const [r, g, b] = parseHex(hex);
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

export function contrastRatio(a: string, b: string): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const [hi, lo] = la > lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}

/** Text colour for content placed on the accent: whichever of black/white contrasts more. */
export function onAccentColor(accent: string): "#000000" | "#FFFFFF" {
  return contrastRatio(accent, "#000000") >= contrastRatio(accent, "#FFFFFF") ? "#000000" : "#FFFFFF";
}

/** Warm ivory body the app renders on (studio theme, light mode). Used for validation and static shells. */
export const BODY_REFERENCE = "#FBF7F4";

export interface AccentCheck {
  ok: boolean;
  accentOnBody: number;
  textOnAccent: number;
  problems: string[];
}

/** Accent must read as a UI element on the light body (3:1) and carry text (4.5:1). */
export function checkAccent(accent: string): AccentCheck {
  const accentOnBody = contrastRatio(accent, BODY_REFERENCE);
  const textOnAccent = contrastRatio(accent, onAccentColor(accent));
  const problems: string[] = [];
  if (accentOnBody < 3) problems.push(`accent contrast on light background is ${accentOnBody.toFixed(2)}:1, needs ≥ 3:1`);
  if (textOnAccent < 4.5) problems.push(`text on accent contrast is ${textOnAccent.toFixed(2)}:1, needs ≥ 4.5:1`);
  return { ok: problems.length === 0, accentOnBody, textOnAccent, problems };
}
